import { useCallback, useMemo, useRef, useState, type CSSProperties, type PointerEvent } from 'react'
import { useAevocamPlayer } from './useAevocamPlayer'
import { AevocamInOutMark } from './AevocamInOutMark'
import { AevocamOutlierMark } from './AevocamOutlierMark'
import { buildOutlierMarks } from './outlierMarks'
import './AevocamScrubber.css'

export type AevocamScrubberProps = {
  className?: string
  /** When `true`, always show attention styling. When `false`, never (overrides hover/focus). When omitted, attention follows hover/focus. */
  attention?: boolean
  tabIndex?: number
}

/**
 * Frame scrubber built from custom visuals (track, thumb, in/out marks). Pointer-driven;
 * keyboard shortcuts are handled on {@link AevocamPlayerShell}.
 */
export function AevocamScrubber({
  className = '',
  attention,
  tabIndex,
}: AevocamScrubberProps) {
  const {
    currentIndex,
    totalPhotos,
    inFrame,
    outFrame,
    markOutliers,
    viewerPhotos,
    loadingStage,
    actions,
  } = useAevocamPlayer()

  const rootRef = useRef<HTMLDivElement>(null)
  const railRef = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState(false)
  const [focused, setFocused] = useState(false)

  const isAttention =
    attention === true || (attention !== false && (hover || focused))

  const max = Math.max(0, totalPhotos - 1)
  const disabled = loadingStage < 2 || totalPhotos < 1

  const scrubMarks = useMemo(() => {
    if (totalPhotos < 1) return null
    const cap = Math.max(totalPhotos - 1, 0)
    const denom = Math.max(cap, 1)
    const clamp = (i: number) => Math.min(Math.max(i, 0), cap)
    const fracIn = inFrame > -1 ? clamp(inFrame) / denom : null
    const fracOut = outFrame > -1 ? clamp(outFrame) / denom : null
    if (fracIn === null && fracOut === null) return null
    return { fracIn, fracOut }
  }, [totalPhotos, inFrame, outFrame])

  const outlierMarks = useMemo(() => {
    if (!markOutliers) return []
    return buildOutlierMarks(viewerPhotos, totalPhotos)
  }, [markOutliers, viewerPhotos, totalPhotos])

  const clampedValue = Math.min(Math.max(currentIndex, 0), max)
  const span = Math.max(max, 0)
  const valueFrac = span > 0 ? clampedValue / span : 0
  const clampedFrac = Math.min(Math.max(valueFrac, 0), 1)

  const commitIndex = useCallback(
    (next: number) => {
      const hi = Math.max(0, totalPhotos - 1)
      const clamped = Math.min(hi, Math.max(0, Math.round(next)))
      if (clamped !== currentIndex) {
        actions.setDirection(clamped < currentIndex)
      }
      actions.scrubToIndex(clamped)
    },
    [totalPhotos, currentIndex, actions],
  )

  const fracFromClientX = useCallback((clientX: number) => {
    const rail = railRef.current
    const root = rootRef.current
    if (!rail || !root) return 0
    const st = getComputedStyle(root)
    let endRPx = cssLengthToPx(
      st.getPropertyValue('--aevocam-scrubber-track-end-radius').trim(),
    )
    if (!Number.isFinite(endRPx) || endRPx <= 0) {
      const th = st.getPropertyValue('--aevocam-scrubber-radius').trim() || '0.45rem'
      endRPx = (cssLengthToPx(th) + 2)
    }
    let shellHalfPx = cssLengthToPx(
      st.getPropertyValue('--aevocam-scrubber-radius').trim(),
    )
    if (!Number.isFinite(shellHalfPx) || shellHalfPx <= 0) {
      const padY = st.getPropertyValue('--aevocam-scrubber-shell-padding-y').trim() || '0.35rem'
      const railH = st.getPropertyValue('--aevocam-scrubber-rail-height').trim() || '2.75rem'
      shellHalfPx = (2 * cssLengthToPx(padY) + cssLengthToPx(railH)) / 2
    }
    const rect = rail.getBoundingClientRect()
    const usable = rect.width - 2 * shellHalfPx - 2 * endRPx
    if (usable <= 0) return 0
    const local = clientX - rect.left - shellHalfPx - endRPx
    return Math.min(1, Math.max(0, local / usable))
  }, [])

  const commitFromClientX = useCallback(
    (clientX: number) => {
      if (disabled || max <= 0) {
        commitIndex(0)
        return
      }
      const frac = fracFromClientX(clientX)
      commitIndex(frac * max)
    },
    [disabled, max, fracFromClientX, commitIndex],
  )

  const handlePointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (disabled) return
      if (e.button !== 0) return
      e.currentTarget.setPointerCapture(e.pointerId)
      commitFromClientX(e.clientX)
    },
    [disabled, commitFromClientX],
  )

  const handlePointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (disabled) return
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
      commitFromClientX(e.clientX)
    },
    [disabled, commitFromClientX],
  )

  const handlePointerUp = useCallback((e: PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }, [])

  const rootClass = useMemo(() => {
    const parts = ['AevocamScrubber']
    if (isAttention) parts.push('Attention')
    if (disabled) parts.push('Disabled')
    if (className) parts.push(className)
    return parts.join(' ')
  }, [isAttention, disabled, className])

  const resolvedTabIndex = tabIndex ?? (disabled ? -1 : 0)

  return (
    <div
      ref={rootRef}
      className={rootClass}
      role='slider'
      tabIndex={resolvedTabIndex}
      aria-label='Frame position'
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={clampedValue}
      aria-disabled={disabled}
      aria-orientation='horizontal'
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <div className='Shell'>
        <div ref={railRef} className='Rail' aria-hidden>
          <div className='Track' />
          {scrubMarks && (
            <div className='Marks'>
              {scrubMarks.fracIn != null && (
                <div
                  className='MarkSlot In'
                  style={
                    { '--mark-fraction': scrubMarks.fracIn } as CSSProperties
                  }
                >
                  <AevocamInOutMark
                    variant='left'
                  />
                </div>
              )}
              {scrubMarks.fracOut != null && (
                <div
                  className='MarkSlot Out'
                  style={
                    { '--mark-fraction': scrubMarks.fracOut } as CSSProperties
                  }
                >
                  <AevocamInOutMark
                    variant='right'
                  />
                </div>
              )}
            </div>
          )}
          {outlierMarks.length > 0 && (
            <div className='OutlierMarks'>
              {outlierMarks.map(mark => (
                <div
                  key={mark.index}
                  className='OutlierMarkSlot'
                  style={{ '--mark-fraction': mark.frac } as CSSProperties}
                >
                  <AevocamOutlierMark />
                </div>
              ))}
            </div>
          )}
          <div
            className='Thumb'
            style={{ '--aevocam-scrubber-value-frac': clampedFrac } as CSSProperties}
          />
        </div>
      </div>
    </div>
  )
}

function cssLengthToPx(raw: string): number {
  const s = raw.trim()
  const n = parseFloat(s)
  if (Number.isNaN(n)) return 0
  if (s.endsWith('rem')) {
    const fs = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
    return n * fs
  }
  if (s.endsWith('em')) {
    const fs = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
    return n * fs
  }
  return n
}
